import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { toast } from 'react-toastify'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import { trackEvent, ANALYTICS_EVENTS } from '../lib/analytics'
import {
  demoSearchQueries,
  demoSearchStats,
  demoSearchSuggestions,
  demoProviders
} from '../utils/demoData'
import {
  Search,
  Play,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Trash2,
  RefreshCw,
  Sparkles,
  TrendingUp,
  Users,
  Loader2
} from 'lucide-react'

interface SearchQuery {
  id: string
  query: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  providers: string[]
  createdAt: string
  completedAt?: string
  results?: SearchResult[]
  aiSummary?: string | null
  briefSummary?: string | null
  // Aggregated AI analysis (unified view)
  aggregated?: {
    brandMentioned: boolean
    mentionRate: number
    consensus: 'strong' | 'weak' | 'none'
    providersQueried: number
  }
}

interface SearchSuggestion {
  id: string
  suggestion: string
  reasoning?: string | null
  source: string
  status: string
  priority: number
  category?: string | null
  createdAt: string
  metadata?: {
    reachPotential?: string
    estimatedVolume?: string
    [key: string]: unknown
  } | null
}

interface SearchResult {
  id: string
  provider: string
  model: string
  response: string
  tokensUsed: number
  latencyMs: number
  createdAt: string
  metadata?: {
    summary?: string
    estimatedCost?: number
    mentions?: unknown[]
    [key: string]: unknown
  }
  // Legacy fields for compatibility
  summary?: string
  estimatedCost?: number
  executionTime?: number
}

interface Statistics {
  totalQueries: number
  completedQueries: number
  failedQueries: number
  pendingQueries: number
  totalResults: number
  totalCost: number
  totalTokens: number
  avgExecutionTime: number
  mentionRate: number
  competitorBrandsCount: number
  providerBreakdown: Array<{
    provider: string
    count: number
    totalCost: number
    totalTokens: number
  }>
}

interface Provider {
  provider: string
  available: boolean
  model?: string
  error?: string
}

// TODO: Add Search Queries Insights Section
// Similar to BrandPerformanceCard insights, but focused on:
// - Which queries to optimize first (high-value, low-performance gaps)
// - Provider-specific strategies (e.g., "You're weak on Gemini - improve Google-indexed content")
// - Query-level optimization tactics
// Example recommendations:
// - "Query 'best X for Y' gets 0 mentions - create dedicated landing page"
// - "You rank #1 on ChatGPT but #5 on Gemini - strengthen topical authority"
// - "3 comparison queries mention competitors but not you - add comparison pages to site"
// See: docs/AI_INSIGHTS_SYSTEM.md (Section-Specific Insights > Search Queries)

export default function SearchIndex() {
  const { activeBrand } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { isDemoMode } = useDemoMode()

  // State
  const [queries, setQueries] = useState<SearchQuery[]>([])
  const [stats, setStats] = useState<Statistics | null>(null)
  const [providers, setProviders] = useState<Provider[]>([])
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [hasAutoGenerated, setHasAutoGenerated] = useState(false)

  // New search form
  const [showNewSearch, setShowNewSearch] = useState(false)
  const [newQuery, setNewQuery] = useState('')
  const [expectedMention, setExpectedMention] = useState(activeBrand?.companyName || '')
  const [executing, setExecuting] = useState(false)

  // Selected query details
  const [selectedQuery, setSelectedQuery] = useState<SearchQuery | null>(null)
  const [queryComparison, setQueryComparison] = useState<any>(null)
  const [aiSummary, setAiSummary] = useState<string | null>(null)

  useEffect(() => {
    if (activeBrand || isDemoMode) {
      fetchData()
      fetchProviders()
      fetchSuggestions()
      setHasAutoGenerated(false) // Reset on brand change
    }
  }, [activeBrand, isDemoMode])

  // Auto-generate more suggestions if we have fewer than 4 after filtering
  useEffect(() => {
    if (!hasAutoGenerated && suggestions.length > 0 && !loadingSuggestions) {
      const filteredSuggestions = suggestions.filter(suggestion => {
        const suggestionText = suggestion.suggestion.toLowerCase().trim()
        return !queries.some(query =>
          query.query.toLowerCase().trim() === suggestionText
        )
      })

      // If we have some suggestions but fewer than 4 after filtering, generate more
      if (filteredSuggestions.length > 0 && filteredSuggestions.length < 4) {
        setHasAutoGenerated(true) // Only do this once
        handleGenerateSuggestions()
      }
    }
  }, [suggestions, queries, hasAutoGenerated, loadingSuggestions])

  // Handle deep link to specific query
  useEffect(() => {
    const state = location.state as { openQueryId?: string }
    if (state?.openQueryId && queries.length > 0 && !selectedQuery) {
      const queryToOpen = queries.find(q => q.id === state.openQueryId)
      if (queryToOpen) {
        handleViewDetails(queryToOpen)
        // Clear the state after opening to prevent re-opening on close
        navigate(location.pathname, { replace: true, state: {} })
      }
    }
  }, [location.state, queries, selectedQuery])

  // Restart stuck pending queries on page load
  useEffect(() => {
    if (!activeBrand || isDemoMode || loading) return

    const restartStuckQueries = async () => {
      // Find queries that are pending (no results at all)
      const stuckQueries = queries.filter(q => q.status === 'pending')

      for (const query of stuckQueries) {
        // Check if query is older than 2 minutes (likely stuck)
        const createdAt = new Date(query.createdAt)
        const now = new Date()
        const ageInMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60)

        if (ageInMinutes > 2) {
          console.log(`[SearchQueries] Restarting stuck query ${query.id} (age: ${ageInMinutes.toFixed(1)} minutes)`)
          try {
            await api.post(`/search-query/${query.id}/analyze`)
            toast.info('Restarting stuck search query...', { autoClose: 2000 })
          } catch (error) {
            console.error(`Failed to restart query ${query.id}:`, error)
          }
        }
      }
    }

    restartStuckQueries()
  }, [queries, activeBrand, isDemoMode, loading])

  // Poll for pending/running queries
  useEffect(() => {
    if (!activeBrand || isDemoMode) return

    const pendingQueries = queries.filter(q =>
      q.status === 'pending' || q.status === 'running'
    )

    if (pendingQueries.length === 0) return

    // Poll every 3 seconds for pending queries
    const pollInterval = setInterval(async () => {
      for (const query of pendingQueries) {
        try {
          const response = await api.get(`/search/query/${query.id}`)
          const updatedQuery = response.data

          // Derive status from results AND summary
          const results = updatedQuery.searchResults || []
          const hasResults = results.length > 0
          const hasErrors = results.some((r: any) => r.error)
          // Check for actual summary content (not just non-null values)
          const hasSummary = (updatedQuery.briefSummary && updatedQuery.briefSummary.trim().length > 0) ||
                            (updatedQuery.aiSummary && updatedQuery.aiSummary.trim().length > 0)

          let status = 'pending'
          if (hasResults) {
            if (hasErrors) {
              status = 'failed'
            } else if (hasSummary) {
              // Only mark as completed if both results AND summary are ready
              status = 'completed'
            } else {
              // Results exist but no summary yet - still processing
              status = 'running'
            }
          }

          // Map provider enum values back to lowercase names
          const enumToProviderMap: Record<string, string> = {
            'OPENAI': 'openai',
            'ANTHROPIC': 'anthropic',
            'GOOGLE': 'gemini',
            'PERPLEXITY': 'perplexity',
            'OTHER': 'other'
          }

          // Derive providers from results (fallback to available providers if no results)
          const queryProviders = results.length > 0
            ? [...new Set(results.map((r: any) => enumToProviderMap[r.provider] || r.provider.toLowerCase()))]
            : providers.filter((p: Provider) => p.available).map((p: Provider) => p.provider)

          // Update only this query in the state
          setQueries(prev => prev.map(q =>
            q.id === query.id
              ? {
                  ...updatedQuery,
                  results,
                  providers: queryProviders,
                  status,
                  completedAt: hasResults ? results[0]?.createdAt : null
                }
              : q
          ))
        } catch (error) {
          console.error(`Failed to poll query ${query.id}:`, error)
        }
      }
    }, 3000)

    return () => clearInterval(pollInterval)
  }, [queries, activeBrand])

  const fetchData = async () => {
    try {
      setLoading(true)

      // Use demo data in demo mode
      if (isDemoMode) {
        setQueries(demoSearchQueries as any)
        setStats(demoSearchStats)
        setLoading(false)
        return
      }

      const [queriesRes, statsRes] = await Promise.all([
        api.get('/search/queries', {
          params: {
            brandId: activeBrand?.id,
            limit: 50,
            includeResults: true
          }
        }),
        api.get(`/search/stats/${activeBrand?.id}`)
      ])

      // Map provider enum values back to lowercase names
      const enumToProviderMap: Record<string, string> = {
        'OPENAI': 'openai',
        'ANTHROPIC': 'anthropic',
        'GOOGLE': 'gemini',
        'PERPLEXITY': 'perplexity',
        'OTHER': 'other'
      }

      // Derive status for each query based on results
      // API returns { queries: [...] } not a raw array
      const queriesWithStatus = (queriesRes.data?.queries || []).map((query: any) => {
        const results = query.searchResults || []
        const hasResults = results.length > 0
        const hasErrors = results.some((r: any) => r.error)
        // Check for actual summary content (not just non-null values)
        const hasSummary = (query.briefSummary && query.briefSummary.trim().length > 0) ||
                          (query.aiSummary && query.aiSummary.trim().length > 0)

        let status = 'pending'
        if (hasResults) {
          if (hasErrors) {
            status = 'failed'
          } else if (hasSummary) {
            // Only mark as completed if both results AND summary are ready
            status = 'completed'
          } else {
            // Results exist but no summary yet - still processing
            status = 'running'
          }
        }

        // Derive providers from results (fallback to available providers if no results)
        const queryProviders = results.length > 0
          ? [...new Set(results.map((r: any) => enumToProviderMap[r.provider] || r.provider.toLowerCase()))]
          : providers.filter((p: Provider) => p.available).map((p: Provider) => p.provider)

        return {
          ...query,
          results,
          providers: queryProviders,
          status,
          completedAt: hasResults ? results[0]?.createdAt : null
        }
      })

      setQueries(queriesWithStatus)
      setStats(statsRes.data || null)
    } catch (error: any) {
      console.error('Failed to fetch data:', error)
      toast.error(`Failed to load search data: ${error.response?.data?.message || error.message}`, {
        position: 'top-right',
        autoClose: 5000,
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchProviders = async () => {
    try {
      // Use demo data in demo mode
      if (isDemoMode) {
        setProviders(demoProviders as any)
        return
      }

      const response = await api.get('/search/providers')
      setProviders(response.data.providers)
    } catch (error) {
      console.error('Failed to fetch providers:', error)
    }
  }

  const fetchSuggestions = async () => {
    try {
      // Use demo data in demo mode
      if (isDemoMode) {
        setSuggestions(demoSearchSuggestions as any)
        return
      }

      // Fetch more than 4 to account for filtering out already-used suggestions
      const response = await api.get(`/search/suggestions/${activeBrand?.id}`, {
        params: {
          status: 'PENDING',
          limit: 10
        }
      })
      const fetchedSuggestions = response.data || []
      setSuggestions(fetchedSuggestions)

      // Only auto-generate if there are ZERO suggestions
      // Don't auto-generate if we have some suggestions, even if less than 4
      if (fetchedSuggestions.length === 0 && !loadingSuggestions) {
        handleGenerateSuggestions()
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error)
    }
  }

  const handleGenerateSuggestions = async () => {
    if (isDemoMode) {
      toast.info('Demo mode: Suggestion generation is disabled. This is preview data only.', { autoClose: 3000 })
      return
    }

    try {
      setLoadingSuggestions(true)
      await api.post(`/search/suggestions/generate/${activeBrand?.id}`, {
        count: 4
      })
      // Refresh suggestions after generation
      const response = await api.get(`/search/suggestions/${activeBrand?.id}`, {
        params: {
          status: 'PENDING',
          limit: 4
        }
      })
      setSuggestions(response.data || [])
    } catch (error: any) {
      console.error('Failed to generate suggestions:', error)
    } finally {
      setLoadingSuggestions(false)
    }
  }

  const handleUseSuggestion = (suggestion: SearchSuggestion) => {
    setNewQuery(suggestion.suggestion)
    setExpectedMention(activeBrand?.companyName || '')
    setShowNewSearch(true)
  }

  const handleDismissSuggestion = async (suggestionId: string) => {
    if (isDemoMode) {
      toast.info('Demo mode: Dismiss action is disabled. This is preview data only.', { autoClose: 3000 })
      return
    }

    try {
      await api.post(`/search/suggestions/${suggestionId}/dismiss`)
      // Remove from local state
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId))
      toast.success('Suggestion dismissed', { autoClose: 2000 })
    } catch (error: any) {
      console.error('Failed to dismiss suggestion:', error)
      toast.error(`Failed to dismiss: ${error.response?.data?.message || error.message}`)
    }
  }

  const handleExecuteSearch = async () => {
    if (isDemoMode) {
      toast.info('Demo mode: Search execution is disabled. This is preview data only.', { autoClose: 3000 })
      return
    }

    if (!newQuery.trim()) {
      toast.error('Please enter a search phrase')
      return
    }

    if (!expectedMention.trim()) {
      toast.error('Please enter an expected mention (brand name)')
      return
    }

    try {
      setExecuting(true)

      // Queue the search for background execution
      // Backend automatically uses primary providers (OpenAI + Claude)
      await api.post('/search/queue', {
        brandId: activeBrand?.id,
        query: newQuery,
        expectedMention: expectedMention.trim()
        // No providers parameter - backend defaults to primary providers
      })

      // Track search query added
      trackEvent(ANALYTICS_EVENTS.SEARCH_QUERY_ADDED, {
        queryLength: newQuery.trim().length
      })

      toast.success('Search queued successfully!', { autoClose: 2000 })

      // Reset form and close modal immediately
      setNewQuery('')
      setExpectedMention('')
      setShowNewSearch(false)

      // Refresh data to show the new query
      fetchData()

    } catch (error: any) {
      console.error('Failed to queue search:', error)
      toast.error(`Failed to queue search: ${error.response?.data?.message || error.message}`)
    } finally {
      setExecuting(false)
    }
  }

  const handleViewDetails = async (query: SearchQuery) => {
    setSelectedQuery(query)

    // Set AI summary from query data (already generated by worker)
    setAiSummary(query.aiSummary || null)

    // Fetch comparison if results exist
    if (query.results && query.results.length > 0) {
      try {
        const response = await api.get(`/search/query/${query.id}/compare`)
        setQueryComparison(response.data)
      } catch (error) {
        console.error('Failed to fetch comparison:', error)
      }
    }
  }

  const formatSummary = (summary: string) => {
    if (!summary) return []

    // Split by newlines and filter for bullet points
    const lines = summary.split('\n').filter(line => line.trim().startsWith('•'))

    return lines.map((line, index) => {
      return {
        key: index,
        content: line.replace(/^•\s*/, '').trim()
      }
    })
  }

  const handleDeleteQuery = async (queryId: string) => {
    if (isDemoMode) {
      toast.info('Demo mode: Delete action is disabled. This is preview data only.', { autoClose: 3000 })
      return
    }

    if (!confirm('Are you sure you want to delete this query?')) return

    try {
      await api.delete(`/search/query/${queryId}`)
      alert('Query deleted successfully')
      fetchData()
    } catch (error: any) {
      alert(`Failed to delete query: ${error.response?.data?.message || error.message}`)
    }
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Preparing' },
      running: { color: 'bg-blue-100 text-blue-800', icon: Loader2, label: 'Analyizing' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Failed' }
    }

    const badge = badges[status as keyof typeof badges] || badges.pending
    const Icon = badge.icon

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className={status === 'running' ? 'h-3 w-3 mr-1 animate-spin' : 'h-3 w-3 mr-1'} />
        {badge.label}
      </span>
    )
  }

  const getProviderBadge = (provider: string) => {
    const colors = {
      openai: 'bg-purple-100 text-purple-800',
      anthropic: 'bg-orange-100 text-orange-800',
      gemini: 'bg-blue-100 text-blue-800'
    }

    const displayNames: Record<string, string> = {
      openai: 'ChatGPT/OpenAI',
      anthropic: 'Claude/Anthropic',
      gemini: 'Google/Gemini'
    }

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${colors[provider as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        {displayNames[provider] || provider}
      </span>
    )
  }

  const getReachPotentialBadge = (reachPotential?: string) => {
    if (!reachPotential) return null

    const badges = {
      'very-high': { color: 'bg-red-100 text-red-800', label: 'Very High Reach', icon: '🔥🔥🔥' },
      'high': { color: 'bg-orange-100 text-orange-800', label: 'High Reach', icon: '🔥🔥' },
      'medium': { color: 'bg-yellow-100 text-yellow-800', label: 'Medium Reach', icon: '🔥' },
      'low': { color: 'bg-gray-100 text-gray-600', label: 'Low Reach', icon: '💡' }
    }

    const badge = badges[reachPotential as keyof typeof badges] || badges.medium

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`} title={badge.label}>
        <span className="mr-1">{badge.icon}</span>
        {badge.label}
      </span>
    )
  }

  // Demo mode placeholder
  if (isDemoMode) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl lg:text-3xl font-bold text-gray-900">AI Search Tracking</h1>
          <p className="mt-1 lg:mt-2 text-sm lg:text-base text-gray-600">
            Monitor how your brand appears in AI assistant responses to real customer questions.
          </p>
        </div>

        {/* Demo Placeholder */}
        <div className="bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 rounded-lg shadow-xl p-8 lg:p-12 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-full p-4 shadow-lg">
                <Search className="h-12 w-12 text-indigo-600" />
              </div>
            </div>

            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Track Your Brand Across AI Assistants
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Test how ChatGPT, Claude, and Gemini respond to customer questions. See where your brand is mentioned, track mention rates, and get AI-powered insights.
            </p>

            {/* Feature Grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-8 text-left">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <Users className="h-8 w-8 text-indigo-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Multi-Provider Testing</h3>
                <p className="text-sm text-gray-600">
                  Query multiple AI providers simultaneously - OpenAI, Anthropic, and Google
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <TrendingUp className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Mention Tracking</h3>
                <p className="text-sm text-gray-600">
                  Track mention rates, positioning, and sentiment across all AI responses
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <Sparkles className="h-8 w-8 text-cyan-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">AI Insights</h3>
                <p className="text-sm text-gray-600">
                  Get AI-generated summaries and key takeaways from every search query
                </p>
              </div>
            </div>

            <a
              href="/register"
              className="inline-flex items-center px-8 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-lg"
            >
              Sign Up to Track Your Brand
              <span className="ml-2">→</span>
            </a>
          </div>
        </div>

        {/* Sample Query Preview */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Sample Search Query</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-start space-x-3">
              <Search className="h-5 w-5 text-gray-400 mt-1" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 mb-2">Query:</p>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                  "What are the best organic snack brands for health-conscious families?"
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-900">ChatGPT</span>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <p className="text-xs text-green-700">Mentioned in response</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-900">Claude</span>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <p className="text-xs text-green-700">Mentioned in response</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">Gemini</span>
                  <XCircle className="h-5 w-5 text-gray-400" />
                </div>
                <p className="text-xs text-gray-600">Not mentioned</p>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <p className="text-sm font-medium text-gray-900 mb-3">Competitors Also Mentioned:</p>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                  NatureBox
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                  Graze
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                  KIND Snacks
                </span>
              </div>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Sparkles className="h-5 w-5 text-indigo-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-indigo-900 mb-1">AI Summary</p>
                  <p className="text-sm text-indigo-800">
                    Your brand was mentioned by 2 out of 3 providers, achieving a 67% mention rate.
                    Both responses highlighted your organic ingredients and family-friendly approach.
                    You appeared alongside 3 competitors, showing strong market presence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Search Tracking Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <Play className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Custom Queries</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Test any question your customers might ask. Add queries manually or use AI-powered suggestions.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <Eye className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Full Responses</h3>
                <p className="text-sm text-gray-600 mt-1">
                  View complete AI responses with highlighted brand mentions and detailed positioning analysis.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <TrendingUp className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Analytics & Trends</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Track mention rates, cost per query, and performance trends across all AI providers.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <Sparkles className="h-5 w-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900">Smart Suggestions</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Get AI-generated query suggestions based on your industry, products, and target audience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-2" />
            <p className="text-gray-600">Loading AI search dashboard...</p>
          </div>
        </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 lg:gap-0">
        <div>
          <h1 className="text-xl lg:text-3xl font-bold text-gray-900">AI Search Tracking</h1>
          <p className="mt-1 lg:mt-2 text-sm lg:text-base text-gray-600">
            Monitor how your brand appears in AI assistant responses to real customer questions.
          </p>
        </div>
        <div className="relative group">
          <button
            onClick={() => setShowNewSearch(!showNewSearch)}
            disabled={isDemoMode}
            className={`px-3 lg:px-4 py-2 text-sm lg:text-base rounded-lg flex items-center space-x-2 transition-all ${
              isDemoMode
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            <Sparkles className="h-4 w-4 lg:h-5 lg:w-5" />
            <span>New Search</span>
          </button>
          {isDemoMode && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              Available after sign up
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
            </div>
          )}
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-4">
          <div className="bg-white p-3 lg:p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm text-gray-600">Total Queries</p>
                <p className="text-xl lg:text-2xl font-bold text-gray-900 mt-1">{stats.totalQueries}</p>
              </div>
              <Search className="h-5 w-5 lg:h-8 lg:w-8 text-indigo-600" />
            </div>
          </div>

          <div className="bg-white p-3 lg:p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm text-gray-600">Mention Rate</p>
                <p className="text-xl lg:text-2xl font-bold text-green-600 mt-1">
                  {stats.mentionRate.toFixed(1)}%
                </p>
              </div>
              <TrendingUp className="h-5 w-5 lg:h-8 lg:w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-3 lg:p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm text-gray-600">Competitor Brands</p>
                <p className="text-xl lg:text-2xl font-bold text-orange-600 mt-1">{stats.competitorBrandsCount}</p>
              </div>
              <Users className="h-5 w-5 lg:h-8 lg:w-8 text-orange-600" />
            </div>
          </div>
        </div>
      )}

      {/* New Search Modal */}
      {showNewSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">New Search</h2>
              <button
                onClick={() => {
                  setShowNewSearch(false)
                  setNewQuery('')
                  setExpectedMention('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Search Phrase */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Phrase <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newQuery}
                  onChange={(e) => setNewQuery(e.target.value)}
                  placeholder="e.g., 'best project management tools for startups'"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  rows={3}
                />
                <p className="mt-1 text-xs text-gray-500">
                  The actual search query to test with AI assistants
                </p>
              </div>

              {/* Expected Mention */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Mention <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={expectedMention}
                  onChange={(e) => setExpectedMention(e.target.value)}
                  placeholder={activeBrand?.companyName || 'Your brand name'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  The brand or company name to look for in AI responses
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  This search will be analyzed by AI assistants in the background. Results typically appear within 30-60 seconds.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewSearch(false)
                  setNewQuery('')
                  setExpectedMention('')
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleExecuteSearch}
                disabled={executing}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center space-x-2"
              >
                <Play className="h-4 w-4" />
                <span>{executing ? 'Executing...' : 'Execute Search'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Query History */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Brand Search Results</h2>
          <button
            onClick={fetchData}
            className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center space-x-1"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>

        <div className="divide-y divide-gray-200">
          {queries.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              <Search className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No searches yet. Execute your first AI search!</p>
            </div>
          ) : (
            queries.map(query => (
              <div key={query.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {getStatusBadge(query.status)}
                      <span className="text-sm text-gray-500">
                        {new Date(query.createdAt).toLocaleString()}
                      </span>
                    </div>

                    <p className="text-gray-900 font-medium mb-2">{query.query}</p>

                    {query.briefSummary && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                        {query.briefSummary}
                      </p>
                    )}

                    {query.status === 'completed' && query.results && query.results.length > 0 && (
                      <div className="flex items-center space-x-3">
                        {/* Use aggregated data if available, otherwise calculate from results */}
                        {(() => {
                          const mentionRate = query.aggregated?.mentionRate ?? (() => {
                            const totalResults = query.results.length
                            const mentionedResults = query.results.filter(r => {
                              const mentions = r.metadata?.mentions || []
                              return mentions.length > 0
                            }).length
                            return totalResults > 0 ? (mentionedResults / totalResults) * 100 : 0
                          })()

                          const brandMentioned = query.aggregated?.brandMentioned ?? mentionRate > 0
                          const consensus = query.aggregated?.consensus || 'none'
                          const providersQueried = query.aggregated?.providersQueried || query.results.length
                          const bgColor = brandMentioned ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'

                          // Consensus colors and labels
                          const consensusConfig = {
                            strong: {
                              color: mentionRate === 100 ? 'text-green-700' : 'text-red-700',
                              label: 'Strong',
                              tooltip: mentionRate === 100
                                ? 'All AI sources consistently mention your brand'
                                : 'Consistently not mentioned - major visibility gap'
                            },
                            weak: {
                              color: 'text-orange-600',
                              label: 'Weak',
                              tooltip: 'Inconsistent visibility - some AI sources mention you, others don\'t'
                            },
                            none: {
                              color: 'text-gray-500',
                              label: 'None',
                              tooltip: 'No mentions detected'
                            }
                          }

                          const config = consensusConfig[consensus as keyof typeof consensusConfig]

                          return (
                            <>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${bgColor}`}>
                                {brandMentioned ? '✓ Mentioned' : '✗ Not Mentioned'}
                              </span>

                              {/* Consensus Visualization */}
                              <div className="flex items-center gap-2" title={config.tooltip}>
                                <span className="text-xs text-gray-600">{Math.round(mentionRate)}%</span>

                                {/* Visual dots showing consensus */}
                                <div className="flex gap-0.5">
                                  {[...Array(providersQueried)].map((_, i) => {
                                    const isMentioned = i < Math.round((mentionRate / 100) * providersQueried)
                                    return (
                                      <div
                                        key={i}
                                        className={`w-2 h-2 rounded-full ${
                                          isMentioned
                                            ? 'bg-green-500'
                                            : 'bg-gray-300'
                                        }`}
                                      />
                                    )
                                  })}
                                </div>

                                {/* Consensus badge */}
                                <span className={`text-xs font-medium ${config.color}`}>
                                  {config.label}
                                </span>
                              </div>
                            </>
                          )
                        })()}
                      </div>
                    )}

                  </div>

                  <div className="flex space-x-2 ml-4">
                    {query.status === 'completed' && (
                      <button
                        onClick={() => handleViewDetails(query)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded"
                        title="View Details"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                    )}
                    <div className="relative group">
                      <button
                        onClick={() => handleDeleteQuery(query.id)}
                        disabled={isDemoMode}
                        className={`p-2 rounded transition-all ${
                          isDemoMode
                            ? 'text-gray-400 cursor-not-allowed opacity-50'
                            : 'text-red-600 hover:bg-red-50'
                        }`}
                        title={isDemoMode ? '' : 'Delete'}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                      {isDemoMode && (
                        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                          Available after sign up
                          <div className="absolute top-full right-4 transform -mt-1 border-4 border-transparent border-t-gray-900"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Real-world AI Searches (Recommendations) */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Recommended Search Phrases</h2>
          <p className="text-sm text-gray-600 mt-1">
            Below are real-world questions people ask AI assistants when searching for brands like yours.
          </p>
        </div>

        <div className="p-6">
          {suggestions.length > 0 ? (
            (() => {
              const filteredSuggestions = suggestions.filter(suggestion => {
                // Filter out suggestions that have already been used as queries
                const suggestionText = suggestion.suggestion.toLowerCase().trim();
                return !queries.some(query =>
                  query.query.toLowerCase().trim() === suggestionText
                );
              });

              return filteredSuggestions.length > 0 ? (
                <>
                  <div className="space-y-3">
                    {filteredSuggestions.slice(0, 10).map((suggestion) => (
                  <div key={suggestion.id} className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer"
                    onClick={() => handleUseSuggestion(suggestion)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-gray-900 font-medium mb-1">{suggestion.suggestion}</p>
                        {suggestion.reasoning && (
                          <p className="text-sm text-gray-600 mb-2">{suggestion.reasoning}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {suggestion.category && (
                            <span className="inline-block px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                              {suggestion.category}
                            </span>
                          )}
                          {getReachPotentialBadge(suggestion.metadata?.reachPotential)}
                        </div>
                      </div>
                      <div className="flex space-x-2 flex-shrink-0">
                        <div className="relative group">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleUseSuggestion(suggestion)
                            }}
                            disabled={isDemoMode}
                            className={`px-3 py-1.5 text-sm rounded flex items-center space-x-1 transition-all ${
                              isDemoMode
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                            }`}
                            title={isDemoMode ? '' : 'Test this search'}
                          >
                            <Play className="h-3 w-3" />
                            <span>Add Query</span>
                          </button>
                          {isDemoMode && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                              Available after sign up
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          )}
                        </div>
                        <div className="relative group">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDismissSuggestion(suggestion.id)
                            }}
                            disabled={isDemoMode}
                            className={`p-1.5 rounded transition-all ${
                              isDemoMode
                                ? 'text-gray-400 cursor-not-allowed opacity-50'
                                : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                            }`}
                            title={isDemoMode ? '' : 'Dismiss'}
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                          {isDemoMode && (
                            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                              Available after sign up
                              <div className="absolute top-full right-4 transform -mt-1 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                  </div>

                  {/* Show loading indicator at bottom if we have fewer than 4 suggestions and still loading */}
                  {filteredSuggestions.length < 4 && loadingSuggestions && (
                    <div className="mt-4 flex items-center justify-center py-4 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                      <div className="text-center">
                        <RefreshCw className="h-6 w-6 animate-spin text-indigo-600 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Generating more recommendations...</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p>All recommendations have been added to search results</p>
                  {/* Generate button only shown in development */}
                  {import.meta.env.DEV && (
                    <button
                      onClick={handleGenerateSuggestions}
                      className="mt-3 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      disabled={loadingSuggestions}
                    >
                      Generate More Recommendations
                    </button>
                  )}
                </div>
              );
            })()
          ) : loadingSuggestions ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-2" />
                <p className="text-gray-600">Generating recommendations...</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Sparkles className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No recommendations available</p>
            </div>
          )}
        </div>
      </div>

      {/* Query Details Modal */}
      {selectedQuery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Search Results</h2>
              <button
                onClick={() => {
                  setSelectedQuery(null)
                  setQueryComparison(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Query Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Query
                </label>
                <input
                  type="text"
                  value={selectedQuery.query}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900"
                />
              </div>

              {/* Key Takeaways */}
              {aiSummary && (
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Sparkles className="h-5 w-5 mr-2 text-indigo-600" />
                    Key Takeaways
                  </h3>

                  <div className="space-y-3">
                    {formatSummary(aiSummary).map((item) => (
                      <div key={item.key} className="flex gap-3 items-start bg-white rounded-lg p-3 shadow-sm">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5">
                          <span className="text-xs font-semibold text-indigo-600">{item.key + 1}</span>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-700 leading-relaxed">{item.content}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Aggregated AI Analysis */}
              {queryComparison && selectedQuery && (
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-700">AI Analysis Results</h3>

                  {/* Overall Status with Consensus */}
                  {(() => {
                    // Use aggregated data from queryComparison (returned by /compare endpoint)
                    const aggregated = queryComparison.aggregated || selectedQuery.aggregated
                    const mentionRate = aggregated?.mentionRate ?? 0
                    const brandMentioned = aggregated?.brandMentioned ?? false
                    const consensus = aggregated?.consensus || 'none'
                    const providersQueried = aggregated?.providersQueried || queryComparison.totals.providers

                    // Consensus insights
                    const getConsensusInsight = () => {
                      if (consensus === 'strong' && mentionRate === 100) {
                        return {
                          icon: '✅',
                          title: 'Strong Consensus - Market Leader',
                          message: 'All AI sources consistently mention your brand. This indicates strong, reliable market presence.',
                          action: 'Maintain current strategy and monitor competitors.',
                          bgColor: 'bg-green-50',
                          borderColor: 'border-green-200',
                          badgeColor: 'bg-green-100 text-green-800',
                          consensusColor: 'text-green-700'
                        }
                      } else if (consensus === 'strong' && mentionRate === 0) {
                        return {
                          icon: '⚠️',
                          title: 'Strong Consensus - Invisible',
                          message: 'Your brand is consistently not mentioned by any AI source. This is a major visibility gap.',
                          action: 'Urgent: Implement AI visibility strategy (LLMS.txt, content distribution, press).',
                          bgColor: 'bg-red-50',
                          borderColor: 'border-red-200',
                          badgeColor: 'bg-red-100 text-red-800',
                          consensusColor: 'text-red-700'
                        }
                      } else if (consensus === 'weak') {
                        return {
                          icon: '🔄',
                          title: 'Weak Consensus - Inconsistent',
                          message: 'Some AI sources mention your brand, others don\'t. This indicates content distribution gaps.',
                          action: 'Identify which AI sources are missing your content and fill those gaps.',
                          bgColor: 'bg-orange-50',
                          borderColor: 'border-orange-200',
                          badgeColor: brandMentioned ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800',
                          consensusColor: 'text-orange-600'
                        }
                      } else {
                        return {
                          icon: '○',
                          title: 'No Consensus Data',
                          message: 'Consensus analysis not available.',
                          action: '',
                          bgColor: 'bg-gray-50',
                          borderColor: 'border-gray-200',
                          badgeColor: 'bg-gray-100 text-gray-800',
                          consensusColor: 'text-gray-500'
                        }
                      }
                    }

                    const insight = getConsensusInsight()

                    return (
                      <div className={`border rounded-lg p-5 ${insight.bgColor} ${insight.borderColor}`}>
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xl">{insight.icon}</span>
                              <p className="text-sm font-semibold text-gray-900">{insight.title}</p>
                            </div>
                            <p className="text-xs text-gray-600">
                              Based on {providersQueried} AI source{providersQueried !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${insight.badgeColor}`}>
                            {brandMentioned ? '✓ Mentioned' : '✗ Not Mentioned'}
                          </span>
                        </div>

                        {/* Consensus Visualization */}
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-2xl font-bold text-gray-900">{Math.round(mentionRate)}%</span>

                          {/* Visual dots */}
                          <div className="flex gap-1">
                            {[...Array(providersQueried)].map((_, i) => {
                              const isMentioned = i < Math.round((mentionRate / 100) * providersQueried)
                              return (
                                <div
                                  key={i}
                                  className={`w-3 h-3 rounded-full ${
                                    isMentioned ? 'bg-green-500' : 'bg-gray-300'
                                  }`}
                                  title={isMentioned ? 'Mentioned' : 'Not mentioned'}
                                />
                              )
                            })}
                          </div>

                          {/* Consensus badge */}
                          <span className={`text-sm font-semibold ${insight.consensusColor}`}>
                            {consensus === 'strong' ? 'Strong Consensus' : consensus === 'weak' ? 'Weak Consensus' : 'No Consensus'}
                          </span>
                        </div>

                        {/* Insight message */}
                        <div className="space-y-2">
                          <p className="text-sm text-gray-700">{insight.message}</p>
                          {insight.action && (
                            <p className="text-sm font-medium text-gray-900">
                              💡 Action: {insight.action}
                            </p>
                          )}
                        </div>
                      </div>
                    )
                  })()}

                  {/* Collapsible Detailed Responses */}
                  <details className="mt-4">
                    <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900 py-2">
                      View detailed AI responses ({queryComparison.providers.length})
                    </summary>
                    <div className="mt-4 space-y-4">
                      {queryComparison.providers.map((result: any) => (
                        <div key={result.provider} className="border-l-2 border-gray-300 pl-4 py-2">
                          <div className="flex items-center space-x-2 mb-2">
                            {getProviderBadge(result.provider)}
                            {result.executionTime && (
                              <span className="text-xs text-gray-500">
                                {(result.executionTime / 1000).toFixed(1)}s
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{result.response}</p>
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
